import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { InputDialogComponent } from '@cube/lib-common';
import { TENANT_CONFIG } from '../../../environments/config';
import { environment } from '../../../environments/environment';
import { Solutions } from '../../models/environment-configuration';
import { User } from '../../models/cube';
import { LanguageOption } from '../../models/cube/language-option.type';
import { CubeService } from '../../services/cube/cube.service';
import { ThemeService } from '../../services/theme/theme.service';
import { UserService } from '../../services/user/user.service';
import { AuthActions } from '../../store/actions/auth.actions';
import { SystemActions } from '../../store/actions/system.actions';
import { selectCurrentLang } from '../../store/selectors/system.selectors';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

@Component({
  selector: 'cube-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
  standalone: false,
})
export class UserMenuComponent implements OnInit {
  @Input() user: User = null;

  @Output() onDepartmentClick: EventEmitter<any> = new EventEmitter<any>();

  activeRole: string;
  activeTenant: string;
  enableAdminTool: boolean;
  enableAppMobile: boolean;
  enableMailingList: boolean;
  enableTenantChange: boolean;
  isOpen: boolean = false;
  language$: Observable<LanguageOption>;
  language: string = 'English';

  private theme: boolean;

  constructor(
    private cubeService: CubeService,
    private elementRef: ElementRef,
    private matDialog: MatDialog,
    private store: Store,
    private themeService: ThemeService,
    private translateService: TranslateService,
    private userService: UserService
  ) {
    this.language$ = this.store.select(selectCurrentLang);
  }

  @HostListener('document:click', ['$event', '$event.target'])
  onClick(event: MouseEvent, targetElement: HTMLElement): void {
    if (!targetElement) {
      return;
    }

    const clickedInside = this.elementRef.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.isOpen = false;
    }
  }

  ngOnInit(): void {
    this.enableAdminTool = this.user.rolesAndModules
      .map((r) => r.name)
      .includes(TENANT_CONFIG.settings.superUserRoleId);
    this.themeService.isDarkTheme.subscribe((theme) => {
      this.theme = theme;
    });
    this.activeRole = this.userService.getActiveRole();
    this.activeTenant = this.userService.getCurrentTenant();
    this.enableTenantChange =
      environment.SOLUTION === Solutions.TI_BSM && this.user.tenants.length > 1;
  }

  doLogout(): void {
    this.userService.doLogout();
  }

  doLogin(): void {
    this.userService.doLogin();
  }

  /* // TEMP: For B2C migration
  goProfile(): void {
    this.userService.goToProfile();
  }
 */
  goAdminTool(): void {
    window.open(environment.ADMIN_TOOL_WEB, '_blank');
  }

  changeTheme(): void {
    this.themeService.setDarkTheme(!this.theme);
  }

  changeProfile(): void {
    const dialogRef = this.matDialog.open(InputDialogComponent, {
      width: '400px',
      height: '165px',
      data: {
        title: this.translateService.instant('menu.roles_selection'),
        confirmLabel: this.translateService.instant('common.buttons.ok'),
        cancelLabel: this.translateService.instant('common.buttons.cancel'),
        inputs: [
          {
            type: 'select',
            label: this.translateService.instant('menu.role_selection'),
            required: true,
            options: this.userService.getAllRoles(),
            value: this.userService.getActiveRole(),
          },
        ],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirm) {
        this.onDepartmentClick.emit();
        // TEMP: For  - B2C migration
        // INFO set active role to store
        const active_role = this.user.roles.find((role) => {
          return role.name == result.data.inputs[0].value;
        });
        this.store.dispatch(
          AuthActions.setActiveRole({ active_role, isPageReload: true })
        );
      }
    });
  }

  changeLang(language: LanguageOption): void {
    this.store.dispatch(SystemActions.ToggleLanguage({ language }));
  }

  changeTenant(): void {
    const dialogRef = this.matDialog.open(InputDialogComponent, {
      width: '400px',
      height: '165px',
      data: {
        title: this.translateService.instant('menu.tenant_selection'),
        confirmLabel: this.translateService.instant('common.buttons.ok'),
        cancelLabel: this.translateService.instant('common.buttons.cancel'),
        inputs: [
          {
            type: 'select',
            label: this.translateService.instant('menu.select_tenant'),
            required: true,
            options: this.user.tenants.map((tenant) => tenant.name),
            value: this.userService.getCurrentTenant(),
            fieldDisplayed: '',
          },
        ],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirm) {
        this.cubeService.cleanCache();
        // TEMP: For  - B2C migration
        // INFO set active tenant to store
        const active_tenant = this.user.tenants.find((tenant) => {
          return tenant.name == result.data.inputs[0].value;
        });
        this.store.dispatch(
          AuthActions.setActiveTenant({ active_tenant, isPageReload: true })
        );
      }
    });
  }

  public openSupportPage(): void {
    window.open('https://support.cube.rina.org', '_blank');
  }
}
